/**
 * Shared connectivity state for the app.
 *
 * ── DISABLED ──
 * The network status probing system has been disabled. It was causing constant
 * false-positive "offline" detections on slow/unstable connections (particularly
 * in Egypt), resulting in banner flicker, badge glitches, and incorrect exam
 * timer pauses. All public API functions remain intact (they always return
 * "online") so downstream consumers don't break.
 *
 * The fetch failure classifier in `lib/api/network-fetch.ts` still uses
 * `getIsOnline()` and `markOffline()`. With the probes disabled, it will always
 * attribute failures to the server rather than to the user's connection, which
 * is the safer default — the app should never blame the user's internet for
 * an ambiguous failure.
 */

export type NetworkQuality = "online" | "unstable" | "offline";

// ── State (frozen to "online") ──────────────────────────────────────────────

let quality: NetworkQuality = "online";
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

// ── Public API (unchanged signatures) ───────────────────────────────────────

/** Always returns "online" while the system is disabled. */
export function getNetworkQuality(): NetworkQuality {
  return quality;
}

/** Always returns null while the system is disabled. */
export function getLastRttMs(): number | null {
  return null;
}

/** Always returns true while the system is disabled. */
export function getIsOnline(): boolean {
  return quality !== "offline";
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Called by the fetch failure classifier when `navigator.onLine` is false.
 * Still functional so the classifier can attribute failures correctly in the
 * rare case where the OS definitively reports no network interface.
 */
export function markOffline(): void {
  if (quality === "offline") return;
  quality = "offline";
  emit();

  // Auto-recover when the OS reports an interface is back.
  if (typeof window !== "undefined") {
    const recover = () => {
      quality = "online";
      emit();
      window.removeEventListener("online", recover);
    };
    window.addEventListener("online", recover);
  }
}
